import { Controller } from "stimulus";

const ACTIVE_BUTTON_CLASS = "active";
const HIDDEN_FORM_CLASS = "hidden";
const MASTER_BL = "master_bl";
const ORGANIZATION_DROPDOWN_PREFIX = "organization_dropdown_";
const ADDRESS_DROPDOWN_PREFIX = "address_dropdown_";
const USER_DROPDOWN_PREFIX = "user_dropdown_";
const API_URL_PREFIX = "/admin/organizations/";
const ADDRESS_URL_SUFFIX = "/address_list";
const USER_URL_SUFFIX = "/user_list";

const DROPDOWN_TYPES = [
  "shipper",
  "consignee",
  "issuer",
  "notify",
  "delivery_agent",
  "requestor",
  "notify_party",
];

export default class extends Controller {
  static targets = ["item", "blForm", "address", "addressDetail", "organization"];

  connect() {
    this.activateItemFromUrlParameters();
    this.disableAddressAndUserDropdowns();
    this.initializeExistingOrganizations();
  }

  activateItemAndDisplayForm(targetBlItem) {
    targetBlItem.classList.add(ACTIVE_BUTTON_CLASS);
    this.displayBlForm();
  }

  displayBlForm() {
    this.blFormTarget.classList.remove(HIDDEN_FORM_CLASS);
  }

  initializeExistingOrganizations() {
    DROPDOWN_TYPES.forEach((dropdownType) => {
      const orgDropdown = document.getElementById(`${ORGANIZATION_DROPDOWN_PREFIX}${dropdownType}`);
      if (!orgDropdown || !orgDropdown.value) return;

      const savedAddressId = document.getElementById(`${ADDRESS_DROPDOWN_PREFIX}${dropdownType}`)
        ?.value;
      const savedUserId = document.getElementById(`${USER_DROPDOWN_PREFIX}${dropdownType}`)?.value;

      this.fetchOrganizationData(
        `${API_URL_PREFIX}${orgDropdown.value}${ADDRESS_URL_SUFFIX}`,
        dropdownType,
        ADDRESS_DROPDOWN_PREFIX,
        "— Remove address —",
        "name",
        savedAddressId
      );
      this.fetchOrganizationData(
        `${API_URL_PREFIX}${orgDropdown.value}${USER_URL_SUFFIX}`,
        dropdownType,
        USER_DROPDOWN_PREFIX,
        "— Remove contact —",
        "display_name",
        savedUserId
      );
    });
  }

  disableAddressAndUserDropdowns() {
    DROPDOWN_TYPES.forEach((dropdownType) => {
      this.disableDropdown(dropdownType, ADDRESS_DROPDOWN_PREFIX);
      this.disableDropdown(dropdownType, USER_DROPDOWN_PREFIX);
    });
  }

  disableDropdown(dropdownType, dropdownPrefix) {
    const target = document.getElementById(`${dropdownPrefix}${dropdownType}`);
    if (target) {
      target.disabled = true;
    }
  }

  enableDropdown(dropdownType, dropdownPrefix) {
    document.getElementById(`${dropdownPrefix}${dropdownType}`).disabled = false;
  }

  organizationDropdownSelected(event) {
    const selectedOrganization = event.target.value;
    const dropdownType = event.target.id.replace(ORGANIZATION_DROPDOWN_PREFIX, "");

    // To avoid multiple address dropdowns active in one time & avoid have multiple address lists
    this.disableAddressAndUserDropdowns();

    this.fetchOrganizationData(
      `${API_URL_PREFIX}${selectedOrganization}${ADDRESS_URL_SUFFIX}`,
      dropdownType,
      ADDRESS_DROPDOWN_PREFIX,
      "— Remove address —",
      "name"
    );
    this.fetchOrganizationData(
      `${API_URL_PREFIX}${selectedOrganization}${USER_URL_SUFFIX}`,
      dropdownType,
      USER_DROPDOWN_PREFIX,
      "— Remove contact —",
      "display_name"
    );
  }

  fetchOrganizationData(
    url,
    dropdownType,
    dropdownPrefix,
    dropdownPlaceholder,
    displayAttr,
    selectedId = null
  ) {
    $.getJSON(url, (data) => {
      if (data.length > 0) {
        this.enableDropdown(dropdownType, dropdownPrefix);
      } else {
        this.disableDropdown(dropdownType, dropdownPrefix);
      }

      if (dropdownPrefix === ADDRESS_DROPDOWN_PREFIX) {
        if (!this.addressDataByType) this.addressDataByType = {};
        this.addressDataByType[dropdownType] = data;
      }

      if (dropdownPrefix === USER_DROPDOWN_PREFIX) {
        if (!this.userDataByType) this.userDataByType = {};
        this.userDataByType[dropdownType] = data;
      }

      const dropdown = document.getElementById(`${dropdownPrefix}${dropdownType}`);

      // Clean previous organization data & add empty option for placeholder
      dropdown.innerText = "";
      dropdown.add(new Option(dropdownPlaceholder, ""));

      data.forEach((d) => dropdown.add(new Option(d[displayAttr], d.id)));

      // Pre-select: saved value, or auto-select if only one entry
      if (selectedId) {
        dropdown.value = selectedId;
      } else if (data.length === 1) {
        dropdown.value = data[0].id;
      }

      // Populate textarea with contact details on page load
      if (dropdown.value && dropdownPrefix === ADDRESS_DROPDOWN_PREFIX) {
        this.setAddressFields(dropdown.value, dropdownType);
      }
      if (dropdown.value && dropdownPrefix === USER_DROPDOWN_PREFIX) {
        this.appendUserContactDetails(dropdown.value, dropdownType);
      }
    });
  }

  appendUserContactDetails(userId, dropdownType) {
    const userData = this.userDataByType?.[dropdownType] || [];
    const selectedUser = userData.find((u) => String(u.id) === String(userId));
    if (!selectedUser) return;

    const addressTarget = this.getTargetUsingRole(this.addressDetailTargets, dropdownType);
    let userLine = selectedUser.display_name || "";
    if (selectedUser.phone) userLine += `  Tel: ${selectedUser.phone}`;
    if (selectedUser.email) userLine += `  Mail: ${selectedUser.email}`;
    if (userLine) {
      addressTarget.value += addressTarget.value ? `\n${userLine}` : userLine;
    }
  }

  onContactFieldSelected(event) {
    const partyType = event.target.dataset.partytype;
    const selectedUserId = event.target.value;
    const addressDropdown = document.getElementById(`${ADDRESS_DROPDOWN_PREFIX}${partyType}`);
    const selectedAddressId = addressDropdown?.value;
    const addressData = this.addressDataByType?.[partyType] || [];
    const selectedAddress = addressData.find((a) => String(a.id) === String(selectedAddressId));
    const userData = this.userDataByType?.[partyType] || [];
    const selectedUser = userData.find((u) => String(u.id) === String(selectedUserId));
    const addressTarget = this.getTargetUsingRole(this.addressDetailTargets, partyType);

    let value = selectedAddress ? this.buildAddressWithContactDetails(selectedAddress) : "";

    if (selectedUser) {
      let userLine = selectedUser.display_name || "";
      if (selectedUser.phone) userLine += `  Tel: ${selectedUser.phone}`;
      if (selectedUser.email) userLine += `  Mail: ${selectedUser.email}`;
      if (userLine) value += value ? `\n${userLine}` : userLine;
    }

    addressTarget.value = value;
  }

  getTargetUsingRole(targets, role) {
    return targets.filter((target) => target.dataset["partytype"] == role)[0];
  }

  getOptionTextSelected(event) {
    if (!event.target.value) return "";
    return $(event.target.selectedOptions).text();
  }

  addressDropdownSelected(event) {
    const { value, id } = event.target;
    const dropdownType = id.replace(ADDRESS_DROPDOWN_PREFIX, "");
    this.setAddressFields(value, dropdownType);
  }

  setAddressFields(addressId, dropdownType) {
    const addressData = this.addressDataByType?.[dropdownType] || [];
    const selectedAddress = addressData.find((address) => String(address.id) === String(addressId));
    if (!selectedAddress) return;

    const addressDetailTarget = this.getTargetUsingRole(this.addressDetailTargets, dropdownType);
    const adddressTarget = this.getTargetUsingRole(this.addressTargets, dropdownType);
    const organizationTarget = this.getTargetUsingRole(this.organizationTargets, dropdownType);
    addressDetailTarget.value = this.buildAddressWithContactDetails(selectedAddress);
    adddressTarget.value = selectedAddress.id;
    organizationTarget.value = selectedAddress.organization_id;
  }

  buildAddressWithContactDetails(address) {
    let result = address.formatted_address;
    const contactParts = [];
    if (address.contact_name) contactParts.push(address.contact_name);
    if (address.phone) contactParts.push(`Tel: ${address.phone}`);
    if (address.email) contactParts.push(`Mail: ${address.email}`);
    if (contactParts.length > 0) result += `\n${contactParts.join("  ")}`;
    return result;
  }

  getTargetElementFromParams() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const blType = urlParams.get("bl_type") || "";
    const bookingId = urlParams.get("booking_id") || "";
    const hasTargetClasses = blType || bookingId;

    if (!hasTargetClasses) return undefined;

    return this.itemTargets.filter((itemTarget) => {
      // on the master_bl we don't pass the bookingId in the params, so if the
      // item contains the "master_bl" class that is the target item
      const isMasterBl = blType === MASTER_BL;

      return (
        isMasterBl ||
        (itemTarget.classList.contains(bookingId) && itemTarget.classList.contains(blType))
      );
    })[0];
  }

  activateItemFromUrlParameters() {
    const targetElement = this.getTargetElementFromParams();
    if (targetElement) {
      this.activateItemAndDisplayForm(targetElement);
    }
  }
}
